import { sessionStatus } from './../../../models/user.interface';
import { Component, EventEmitter, OnInit, OnDestroy, Output, ViewChildren } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import Swal from "sweetalert2";
import { fromEvent, Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AgentService } from '../../../services/agent.service';
import { AuthService } from '../../../services/auth.service';
import { MatSnackBar } from '@angular/material';

const TIME_DISCONNECTION = 5000; //5 sec
const POLLING_INTERVAL = 5000; //5 sec
const SESSION_STATUS_ACK_TIMEOUT = 10000;

@Component({
  selector: 'app-user-availability',
  templateUrl: './user-availability.component.html',
  styleUrls: ['./user-availability.component.scss']
})
export class UserAvailabilityComponent implements OnInit, OnDestroy {
  @Output() sessionStatusOutput = new EventEmitter<sessionStatus>();
  @ViewChildren(NgbDropdown) dropdown: NgbDropdown;
  public userSessionStatus: sessionStatus;
  public availabilityStatus: Array<sessionStatus> = [
    { id: 1, name: "online" },
    { id: 2, name: "reserved" },
    { id: 4, name: "offline" },
    { id: 5, name: "undefined" }
  ];
  public offlineSessionStatus: sessionStatus = { id: 4, name: "offline" };
  public loadingSessionStatus: sessionStatus = { id: 5, name: "undefined" };
  public agentQueueTypes: Array<any> = [];
  private agentId: number;
  private role: number;
  public emptyQueueTooltip: string;
  public unlockButton: boolean = false;
  private _unsub$ = new Subject<boolean>();
  private _pollingUnsub$ = new Subject<boolean>();
  private _pendingSessionStatus: sessionStatus = null;
  private _pendingSessionFallback: sessionStatus = null;
  private _sessionStatusAckTimer: any = null;

  constructor(
    public auth: AuthService,
    public agentService: AgentService,
    private translate: TranslateService,
    private _snackBar: MatSnackBar
  ) {
    this.emptyQueueTooltip = this.translate.instant('admin.trobbitlive.module.agents.agentAvailability.emptyQueueTooltip');
  }

  ngOnInit() {
    this.agentSocketIsListening();

    fromEvent(window, 'offline').pipe(takeUntil(this._unsub$)).subscribe((_) => {
      this.openSnackBar('Sin conexión a internet  ⚠️');
      this.userSessionStatus = { id: 5, name: "undefined" };
      this.agentService.agentSessionStatus = this.userSessionStatus;
      this.unlockButton = false;

      //Timeout: valida conexión real antes de forzar reload
      //Solo recarga si la pestaña está visible y sin conexión (evita recargas por suspensión de app en iOS/móvil)
      const timeoutId = setTimeout(() => {
        if (!navigator.onLine && document.visibilityState === 'visible') {
          window.location.reload();
        }
      }, TIME_DISCONNECTION);

      fromEvent(window, 'online').pipe(takeUntil(this._unsub$)).subscribe((_) => {
        this._snackBar.dismiss();
        clearTimeout(timeoutId);
        this.initSessionStatus(this.loadingSessionStatus);
      });
    });

    this.initSessionStatus(this.loadingSessionStatus);
  }

  ngOnDestroy() {
    this.clearPendingSessionStatus();
    this._pollingUnsub$.next(true);
    this._pollingUnsub$.complete();
    this._unsub$.next(true);
    this._unsub$.complete();
  }

  initSessionStatus(userSessionStatus?: sessionStatus) {
    this.unlockButton = false;
    this.role = this.auth.user.role.id;
    this.userSessionStatus = userSessionStatus;
    if (this.role === 5) {
      this.agentId = this.auth.user.id;
      // Red de seguridad: el estado real solo llega por broadcast del socket. Si esa
      // respuesta se pierde (handshake, reconexion), sin este polling el widget queda
      // en "cargando" para siempre y el boton nunca se habilita. El primer tick sale
      // recien a POLLING_INTERVAL, asi que no reintroduce la carrera de 122c0297.
      // NO BORRAR: se perdio una vez en un merge de reconciliacion (ab3faac5).
      this.startSessionPolling();
      this.agentService.getAgent(this.agentId)
        .subscribe(
          agent => {
            if (agent && agent.queues && agent.queues.length > 0) {
              this.agentQueueTypes = agent.queues;
              this.unlockButton = true;
            }
          },
          error => console.error('[initSessionStatus] Error obteniendo agente:', error),
        );
    }
  }

  async changeSessionStatus(status: sessionStatus) {
    const currentStatusId = this.userSessionStatus ? this.userSessionStatus.id : null;
    if (currentStatusId !== status.id || currentStatusId === 5) {
      if (status.id === 4) return this.showSessionStatusAlert();

      this.beginPendingSessionStatus(status);
      this.startSessionPolling(); // Reset polling clock to avoid race condition with immediate poll
      try {
        const emitted = await this.agentService.setSessionStatus({ agentId: this.agentId, sessionStatus: status });
        if (emitted === false) this.rollbackPendingSessionStatus();
      } catch (error) {
        console.error('[changeSessionStatus] Error cambiando estado de sesión:', error);
        this.rollbackPendingSessionStatus();
      }
    }
  }

  showSessionStatusAlert() {
    Swal.fire({
      title: `${this.translate.instant('admin.trobbitlive.module.agents.agentAvailability.onChangeOfflineTitle')}`,
      html: ` <p>${this.translate.instant('admin.trobbitlive.module.agents.agentAvailability.onChangeOffline')}.</p>
              <p>${this.translate.instant('admin.trobbitlive.conversations.closeAllConfirmation')}</p>`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#01BEFF',
      cancelButtonColor: '#AAAAAA',
      confirmButtonText: 'Aceptar',
      cancelButtonText: `Cancelar`
    }).then(async (result) => {
      if (result.value) {
        this.beginPendingSessionStatus(this.offlineSessionStatus);
        this.startSessionPolling(); // Reset polling clock to avoid race condition with immediate poll
        try {
          const emitted = await this.agentService.setSessionStatus({ agentId: this.agentId, sessionStatus: this.offlineSessionStatus });
          if (emitted === false) this.rollbackPendingSessionStatus();
        } catch (error) {
          console.error('[showSessionStatusAlert] Error cambiando estado de sesión:', error);
          this.rollbackPendingSessionStatus();
        }
      }
    });
  }

  capitalizeFirstletter(string: string) {
    if (string) { return string.charAt(0).toUpperCase() + string.slice(1); }
  }

  agentSocketIsListening() {
    this.agentService.agentSessionStatus$.pipe(takeUntil(this._unsub$)).subscribe((sessionStatus: sessionStatus) => {
      this.applyResolvedSessionStatus(sessionStatus);
    });
  }

  private beginPendingSessionStatus(status: sessionStatus): void {
    const currentServiceStatus = this.agentService.agentSessionStatus;
    const previousStatus = this.userSessionStatus && this.userSessionStatus.id !== 5
      ? this.userSessionStatus
      : currentServiceStatus;
    this.clearPendingSessionStatus();
    this._pendingSessionStatus = status;
    this._pendingSessionFallback = previousStatus && previousStatus.id !== 5
      ? previousStatus
      : this.offlineSessionStatus;
    this.sessionStatusOutput.emit(this.loadingSessionStatus);
    this.userSessionStatus = this.loadingSessionStatus;
    this.unlockButton = false;
    this._sessionStatusAckTimer = setTimeout(() => {
      this.rollbackPendingSessionStatus();
    }, SESSION_STATUS_ACK_TIMEOUT);
  }

  private rollbackPendingSessionStatus(): void {
    if (!this._pendingSessionStatus) return;
    const fallbackStatus = this._pendingSessionFallback || this.offlineSessionStatus;
    this.clearPendingSessionStatus();
    this.applyResolvedSessionStatus(fallbackStatus);
    this.agentService.agentSessionStatus = fallbackStatus;
  }

  private clearPendingSessionStatus(): void {
    if (this._sessionStatusAckTimer) {
      clearTimeout(this._sessionStatusAckTimer);
      this._sessionStatusAckTimer = null;
    }
    this._pendingSessionStatus = null;
    this._pendingSessionFallback = null;
  }

  private applyResolvedSessionStatus(sessionStatus: sessionStatus): void {
    if (!sessionStatus) return;
    if (this._pendingSessionStatus && sessionStatus.id !== 5) {
      this.clearPendingSessionStatus();
    }
    this.sessionStatusOutput.emit(sessionStatus);
    this.userSessionStatus = sessionStatus;
    this.unlockButton = this.hasAgentQueues() && sessionStatus.id !== 5;
  }

  private hasAgentQueues(): boolean {
    return this.agentQueueTypes && this.agentQueueTypes.length > 0;
  }

  /**
   * Polling continuo que envía el estado id 5 (undefined) al servidor
   * para obtener de vuelta la sesión real del agente y mantenerla actualizada.
   * Inicia con un delay de POLLING_INTERVAL para evitar que el primer tick
   * colisione con un cambio de estado manual en curso (race condition).
   */
  startSessionPolling() {
    this._pollingUnsub$.next(true);
    timer(POLLING_INTERVAL, POLLING_INTERVAL).pipe(takeUntil(this._pollingUnsub$), takeUntil(this._unsub$)).subscribe(() => {
      this.agentService.setSessionStatus({ agentId: this.agentId, sessionStatus: this.loadingSessionStatus })
        .catch(error => console.error('[startSessionPolling] Error en polling de estado:', error));
    });
  }

  openSnackBar(message: string) {
    this._snackBar.open(message, null, {
      duration: null,
    });
  }
}
